<template>
  <div style="min-height: calc(100vh - 158px);">
    <div v-if="loading" style="position: fixed; top: 0; bottom: 0; left: 0; right: 0; background: black; z-index: 9999; opacity: 0"></div>
    <div>
      <div style="background: #DBE7F2" class="p-2">
        <div class="d-flex flex-wrap justify-content-between align-items-center">
          <h3>{{ isCreatePage ? 'Imported Cash and Bank Transaction' : 'Edit Cash and Bank Transaction' }}</h3>
          <div class="d-flex gap-1">
            <a
                download
                href="/file/import-cash-bank-transactions.xlsx"
                style="min-width: 64px;"
                class="btn btn-outline-secondary waves-effect waves-float waves-light"
            >Download Sample</a>
            <button @click="goToList" class="btn btn-close"></button>
          </div>
        </div>

        <div class="row p-2" v-if="isCreatePage">
          <div class="col-12 col-sm-6 col-md-5 col-lg-4">
            <label for="colFormLabel" class="col-form-label">Account Head</label>
            <v-select
                placeholder="Select Roles"
                v-model="accountHeadId"
                :options="accountHeads"
                label="name"
            />
          </div>
          <div v-show="accountHeadId" class="col-12 col-sm-6 col-md-4 col-lg-2 mt-auto">
            <button @click="handleClick" class="btn btn-primary waves-effect waves-float waves-light">
              Browse
            </button>
          </div>
        </div>
      </div>

      <div v-if="isUploading" class="row mt-4">
        <div class="col-md-6">
          <label>{{ fileName }}</label>
          <div class="progress progress-bar-primary">
            <div
                class="progress-bar"
                role="progressbar"
                aria-valuemax="100"
                :style="{ width: progress + '%' }"
                :aria-describedby="fileName"
            ></div>
          </div>
        </div>
      </div>

      <input type="file" class="d-none" ref="uploader" @change="handleFileUpload">

      <div v-if="loading" class="spinner-border text-success mx-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <template v-if="sheet.id">
        <Ledger
            :ledgers="sheet.ledgers"
            :contactProfiles="contactProfiles"
            :businesses="businesses"
            :costCentres="costCentres"
            :projects="projects"
            :projectActivity="projectActivity"
            :cashAndBack="cashAndBack"
            @onclickUpdate="onclickUpdate"
            @onclickDelete="onclickDelete"
        />
      </template>

      <div style="background: #DBE7F2" class="p-2">
      <button
          :disabled="loading"
          v-if="sheet.id"
          @click="onClickStore"
          class="btn btn-primary"
      >
        {{ loading ? 'Saving...' : 'Save' }}
      </button>
      </div>
    </div>
    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Ledger from '@/components/molecule/cash-bank-import/Ledger'
import Loader from '@/components/atom/LoaderComponent'
import handleJournal from '@/services/modules/journal'
import handleContact from '@/services/modules/contact'
import handleCBusinesses from '@/services/modules/businesses'
import handleCostCentres from '@/services/modules/ngo/costCentre'
import handleProjects from '@/services/modules/procurement/project'
import handleReceipt from '@/services/modules/receipt'

const showError = inject('showError')
const showSuccess = inject('showSuccess')
const route = useRoute()
const router = useRouter()
const store = useStore()

const {
  updateCashAndBankIndividual,
  removeIndividual,
  storeCashBankIndividual,
  fetchSheet,
  uploadCashAndBankTransaction,
  loading,
} = handleJournal()

const { fetchContactProfiles } = handleContact()
const { fetchBusinessList } = handleCBusinesses()
const { fetchCostCentreList } = handleCostCentres()
const { fetchProjects, fetchProjectActivity } = handleProjects()
const { fetchCashAndBank } = handleReceipt()

const sheet = ref({})
const selectedFile = ref(null)
const fileName = ref('')
const accountHeadId = ref('')
const isUploading = ref(false)
const saveNewLoader = ref(false)
const uploader = ref(null)
const businesses = ref([])
const costCentres = ref([])
const projects = ref([])
const contactProfiles = ref([])
const activities = ref([])
const projectActivity = ref([])
const cashAndBack = ref([])
const accountHeads = ref([])

const companyId = computed(() => route.params.companyId)
const uploadLogId = computed(() => route.params.uploadLogId)
const isLoading = computed(() => loading.value || saveNewLoader.value)
const start = computed(() => route.query.start)
const end = computed(() => route.query.end)
const isCreatePage = computed(() => route.name === 'import-cash-bank-transaction')
const progress = computed(() => store.state.progress)

function onClickStore() {
  const storeValidity = checkStoreValidity()
  if (!storeValidity) {
    showError('Manually fix all entry first')
    return
  }
  findUploadableItem()
}

const getAccountHeads = async () => {
  try {
    loading.value = true;
    let res = await fetchCashAndBank(route.params.companyId);
    if (!res.status) {
      showError(res.message);
      return
    }
    accountHeads.value = res.data;
  } catch (err) {
    showError("Something is wrong. Check your connectivity!!");
  } finally {
    loading.value = false;
  }
}

function findUploadableItem() {
  let item = null
  Object.keys(sheet.value.ledgers).forEach(key => {
    if (!item && sheet.value.ledgers[key].status === 'pending') {
      item = { item: sheet.value.ledgers[key], key }
    }
  })

  if (!item) {
    showSuccess('All item upload finish')
    loading.value = false
    setTimeout(() => {
      goToList()
    }, 1000)
    return
  }

  storeLedger(item)
}

async function storeLedger(item) {
  const data = {
    company_id: companyId.value,
    group_key: item.key,
  }

  try {
    loading.value = true
    const res = await storeCashBankIndividual(sheet.value.id, data)
    if (!res.status) {
      showError(res.message)
    }

    setTimeout(() => {
      item.item.status = 'Completed'
      findUploadableItem()
    },3000)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

function checkStoreValidity() {
  let option = true
  Object.keys(sheet.value.ledgers).forEach(key => {
    sheet.value.ledgers[key].ledgers.forEach(ledger => {
      if (
          ledger.validation_account_head === false ||
          ledger.validation_date === false ||
          ledger.validation_same_head_mop === false ||
          ledger.validation_debit_credit === false
      ) {
        option = false
      }
    })
  })
  return option
}

async function onclickUpdate(data) {
  const fd = new FormData()
  fd.append('company_id', companyId.value)
  fd.append('voucher_no', data.keyName)
  fd.append('ledgers', JSON.stringify(data.ledger.ledgers))

  try {
    loading.value = true
    const res = await updateCashAndBankIndividual(sheet.value.id, fd)
    if (!res.status) {
      return showError(res.message)
    }
    getSheetData()
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

async function onclickDelete(data) {
  const query = `company_id=${companyId.value}&voucher_no=${data.keyName}`

  try {
    loading.value = true
    const res = await removeIndividual(sheet.value.id, query)
    if (!res.status) {
      return showError(res.message)
    }
    getSheetData()
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

async function getSheetData() {
  const uploadId = uploadLogId.value ?? sheet.value.id

  try {
    loading.value = true
    const res = await fetchSheet(uploadId, `?company_id=${companyId.value}`)
    if (!res.status) {
      return showError(res.message)
    }
    sheet.value = res.data
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

async function uploadJournalOnServer(data) {
  try {
    isUploading.value = true
    const res = await uploadCashAndBankTransaction(data)
    if (!res.status) {
      return showError(res.message)
    }
    sheet.value = res.data
    selectedFile.value = null
    fileName.value = ''
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    isUploading.value = false
    store.commit('updateProgress', 0)
  }
}

function handleClick() {
  if (isCreatePage.value) {
    onClickBrowse()
  }
  if (!isCreatePage.value){
    goToList()
  }
}

function goToList() {
  router.push({ name: 'import-cash-bank-transaction-list', params: route.params, query: route.query })
}

function onClickBrowse() {
  uploader.value.click()
}

function handleFileUpload(event) {
  selectedFile.value = event.target.files[0]
  fileName.value = event.target.files[0].name

  const fd = new FormData()
  fd.append('company_id', companyId.value)
  fd.append('note', ' ')
  fd.append('mop_account_head_id', accountHeadId.value.id)
  fd.append('mop_references', ' ')
  fd.append('sheet', selectedFile.value)

  uploadJournalOnServer(fd)
}

onMounted(() => {
  const id = companyId.value
  const companyQuery = `?company_id=${id}`
  const query = `${companyQuery}&q=&contact_id`

  if (!isCreatePage.value) {
    getSheetData()
  }

  getAccountHeads()

  Promise.all([
    fetchContactProfiles(query).then(res => { if (res.data) contactProfiles.value = res.data }),
    fetchBusinessList(companyQuery).then(res => { if (res.data) businesses.value = res.data }),
    fetchCostCentreList(companyQuery).then(res => { if (res.data) costCentres.value = res.data }),
    fetchProjects(companyQuery).then(res => { if (res.data) projects.value = res.data }),
    fetchProjectActivity(companyQuery).then(res => { if (res.data) projectActivity.value = res.data }),
    fetchCashAndBank(id).then(res => { if (res.data) cashAndBack.value = res.data }),
  ])
})
</script>

<style scoped>
hr {
  margin: 0 -3%;
  width: 106%;
}
.btn-close {
  width: 30px;
  height: 30px;
  background-color: #E4E6EB;
  border-radius: 50%;
}
.btn-close:hover {
  background-color: #D8DADF;
}
</style>